import { useState } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import classNames from "classnames";

import PdfIcon from "assets/icons/pdf.svg?react";
import DownloadIcon from "assets/icons/download.svg?react";
import ConfirmationPreviewModal, {
  ConfirmationPreviewSource,
} from "components/ConfirmationPreviewModal";

import dispatchChartService from "api/dispatchChartService";
import {
  EParcelConfirmationFileKind,
  IParcelEvidence,
} from "dto/Mailroom/IDispatchChart";

import {
  downloadParcelEvidenceOriginal,
  fetchParcelEvidence,
  isXmlEvidence,
  receiptLabelKey,
  saveBlob,
} from "./evidence";
import styles from "./styles.module.scss";

interface Props {
  /** Id przesyłki wychodzącej albo jej odbiorcy (pozycja listy). */
  parcelId: string;
  className?: string;
}

const fmtDate = (d?: string | null) =>
  d ? moment(d).format("YYYY.MM.DD") : "";

/**
 * Sekcja „Doręczenie" metryki przesyłki wychodzącej — port „Metryki przesyłki" z Magistratu
 * (Data nadania + potwierdzenie nadania, Data odbioru/zwrotu + „Podgląd ZPO", powód zwrotu,
 * ePUAP UPP/UPD, dowody e-Doręczeń). Skany deszyfrowane kluczem firmy, dowody jawne.
 */
const ParcelDeliveryPanel = ({ parcelId, className }: Props) => {
  const { t } = useTranslation();
  const { data, isLoading } = dispatchChartService.useGetDeliveryInfo(parcelId);
  const info = data?.data;

  const [preview, setPreview] = useState<{
    title: string;
    source: ConfirmationPreviewSource;
  } | null>(null);
  const [busyEvidence, setBusyEvidence] = useState<string | null>(null);

  if (isLoading || !info) return null;
  // Przesyłki spoza obiegu biura (np. Nota→Nota) mają doręczenie per odbiorca w historii
  // metryki — sekcja tylko wtedy, gdy jest co pokazać (skan / dowody).
  if (
    !info.ViaMailroom &&
    !info.HasReceptionConfirmation &&
    !info.HasDispatchNote &&
    info.Evidences.length === 0
  )
    return null;

  const openScan = (kind: EParcelConfirmationFileKind, title: string) =>
    setPreview({
      title,
      source: { kind: "confirmation", parcelId: info.ParcelId, fileKind: kind },
    });

  const openEvidence = async (e: IParcelEvidence) => {
    if (busyEvidence) return;
    setBusyEvidence(e.Id);
    try {
      const file = await fetchParcelEvidence(e);
      if (!file) return;
      if (file.previewable) {
        setPreview({
          title: e.Label,
          source: {
            kind: "blob",
            blob: file.blob,
            fileName: file.fileName,
            contentType: file.contentType,
          },
        });
      } else {
        saveBlob(file.blob, file.fileName);
      }
    } finally {
      setBusyEvidence(null);
    }
  };

  const downloadOriginal = async (e: IParcelEvidence) => {
    if (busyEvidence) return;
    setBusyEvidence(e.Id);
    try {
      await downloadParcelEvidenceOriginal(e);
    } finally {
      setBusyEvidence(null);
    }
  };

  const receivedLabel = info.IsReturned
    ? t("metric.delivery.returnedDate")
    : t("metric.delivery.receivedDate");

  return (
    <div className={classNames(styles.panel, className)}>
      <div className={styles.title}>{t("metric.delivery.title")}</div>

      <div className={styles.rows}>
        {info.DeliveryKindName && (
          <div className={styles.row}>
            <span className={styles.label}>
              {t("metric.delivery.deliveryKind")}
            </span>
            <span className={styles.value}>{info.DeliveryKindName}</span>
          </div>
        )}

        <div className={styles.row}>
          <span className={styles.label}>
            {t("metric.delivery.dispatchDate")}
          </span>
          <span className={styles.value}>
            {info.DispatchDate ? (
              <>
                {fmtDate(info.DispatchDate)}
                {info.ChartSignature && (
                  <span className={styles.muted}>
                    {" "}
                    ({t("metric.delivery.chart")} {info.ChartSignature})
                  </span>
                )}
              </>
            ) : (
              <span className={styles.muted}>
                {t("metric.delivery.notDispatched")}
              </span>
            )}
            {info.HasDispatchNote && (
              <span
                className={styles.link}
                onClick={() =>
                  openScan(
                    EParcelConfirmationFileKind.DispatchNote,
                    t("metric.delivery.previewDispatchNote")
                  )
                }
              >
                {t("metric.delivery.previewDispatchNote")}
              </span>
            )}
          </span>
        </div>

        <div className={styles.row}>
          <span
            className={classNames(
              styles.label,
              info.IsReturned && styles.returned
            )}
          >
            {receivedLabel}
          </span>
          <span className={styles.value}>
            {info.ReceivedDate ? (
              <span className={classNames(info.IsReturned && styles.returned)}>
                {fmtDate(info.ReceivedDate)}
              </span>
            ) : info.PickupNotCollected ? (
              <span className={styles.returned}>
                {t("metric.delivery.notCollected")}
              </span>
            ) : (
              <span className={styles.muted}>
                {t("metric.delivery.notDelivered")}
              </span>
            )}
            {info.PickupByName && !info.IsReturned && (
              <span className={styles.muted}>
                {" "}
                · {t("metric.delivery.pickedUpBy")}: {info.PickupByName}
              </span>
            )}
            {info.HasReceptionConfirmation && (
              <span
                className={styles.link}
                onClick={() =>
                  openScan(
                    EParcelConfirmationFileKind.Reception,
                    t(receiptLabelKey(info.DeliveryCategory))
                  )
                }
              >
                {t(receiptLabelKey(info.DeliveryCategory))}
              </span>
            )}
          </span>
        </div>

        {info.IsReturned && (
          <div className={styles.row}>
            <span className={styles.label}>
              {t("metric.delivery.returnReason")}
            </span>
            <span className={styles.value}>
              {info.ReturnAnnotation || t("metric.delivery.returned")}
            </span>
          </div>
        )}
      </div>

      {info.Evidences.length > 0 && (
        <>
          <div className={styles.subTitle}>
            {t("metric.delivery.evidences")}
          </div>
          <div className={styles.evidenceList}>
            {info.Evidences.map((e) => (
              <div key={e.Id} className={styles.evidenceRow}>
                <div className={styles.evidenceInfo}>
                  <div className={styles.evidenceName}>{e.Label}</div>
                  <div className={styles.evidenceDate}>
                    {e.Date ? moment(e.Date).format("YYYY.MM.DD HH:mm") : "—"}
                    {e.Type && (
                      <span className={styles.muted}> · {e.Type}</span>
                    )}
                  </div>
                </div>
                {e.HasFile && (
                  <div className={styles.evidenceActions}>
                    <div
                      className={classNames(
                        styles.iconBtn,
                        busyEvidence === e.Id && styles.busy
                      )}
                      title={t("metric.delivery.openEvidence")}
                      onClick={() => openEvidence(e)}
                    >
                      <PdfIcon />
                    </div>
                    {isXmlEvidence(e) && (
                      <div
                        className={classNames(
                          styles.iconBtn,
                          busyEvidence === e.Id && styles.busy
                        )}
                        title={t("metric.delivery.downloadOriginal")}
                        onClick={() => downloadOriginal(e)}
                      >
                        <DownloadIcon />
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      {preview && (
        <ConfirmationPreviewModal
          title={preview.title}
          source={preview.source}
          onClose={() => setPreview(null)}
        />
      )}
    </div>
  );
};

export default ParcelDeliveryPanel;
